/**
 * Filter drawer for the Dashboard Users view.
 * Uses the shared FilterDrawer for the outer drawer shell.
 *
 * Filter fields:
 * - Organizations (SUPER_ADMIN only)
 * - Role (multiselect — Super Admin option only visible to Super Admins)
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useState } from "react";
import { FilterDrawer, useFilterDrawerContext } from "../../../components/filters/FilterDrawer";
import {
  getInStatusFilter,
  getInitialFilterValues,
  SearchInput,
  toSearchInputArray,
} from "../../../configuration/filters/searchInput";
import { MultiselectInput, MultiselectValue } from "../../../components/Multiselect";
import { dashboardUserRoleRender } from "../../../utils/searchField.util";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../../components/filters/filterStyles";
import { useAuthUser } from "../../../hooks/useAuthUser";

export const path = "filter";
const filterFormId = "dashboard-users-filter-form";

type DashboardUsersFilterProps = {
  isOpened: boolean;
  filters: SearchInput<GraphQL.DashboardUserSearchFields>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.DashboardUserSearchFields>[]) => void;
};

export function DashboardUsersFilter({ isOpened, filters, onClose, onApply }: DashboardUsersFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} filters={filters} onClose={onClose} onApply={onApply} />
  );
}

export function DashboardUsersFilterInner() {
  const { classes } = useFilterStyles();
  const user = useAuthUser();

  const { initialFilters, onClose, onApply } = useFilterDrawerContext<GraphQL.DashboardUserSearchFields>();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.DashboardUserSearchFields.CustomerPublicId, initialFilters),
    },
  });

  const availableRoles = user.is.SUPER_ADMIN
    ? allRoles
    : allRoles.filter((role) => role.id !== GraphQL.DashboardUserRole.SuperAdmin);

  const [roleFilter, setRoleFilter] = useState<GraphQL.DashboardUserRole[]>(() =>
    getInitialFilterValues<GraphQL.DashboardUserSearchFields, GraphQL.DashboardUserRole>(
      GraphQL.DashboardUserSearchFields.Role,
      initialFilters
    )
  );

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.DashboardUserSearchFields.CustomerPublicId, customerPublicId),
      getInStatusFilter(GraphQL.DashboardUserSearchFields.Role, roleFilter),
    ]);

    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
    setRoleFilter([]);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        {user.is.SUPER_ADMIN && <OrganizationFilter {...getInputProps("selectedCustomer")} />}
        <MultiselectInput
          label="Role"
          items={availableRoles}
          value={roleFilter.map((value) => ({ id: value, label: dashboardUserRoleRender(value) }))}
          onValueChange={(newValue) => {
            setRoleFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setRoleFilter((current) => current.filter((role) => role !== valueToDelete.id));
          }}
        />
      </form>
    </Stack>
  );
}

const allRoles: MultiselectValue<GraphQL.DashboardUserRole>[] = Object.values(GraphQL.DashboardUserRole).map(
  (role) => ({ id: role, label: dashboardUserRoleRender(role) })
);
